import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export interface MobileAction {
    key: string;
    label: string;
    icon: IconDefinition;
    /** A route to navigate to. Ignored when `onClick` is given. */
    to?: string;
    onClick?: () => void;
    /** The one action the screen exists for — gets the accent fill. */
    primary?: boolean;
    /** For actions that switch what the page is showing, e.g. the event tabs. */
    active?: boolean;
    disabled?: boolean;
}

interface MobileActionBarProps {
    actions: MobileAction[];
}

/**
 * The fixed row of quick actions along the bottom of a phone screen.
 *
 * Nothing here is new: every action also lives in the header, the tab bar or the
 * form column further up the page. The bar's whole job is to keep them within a
 * thumb's reach after the page has been scrolled — so it disappears from `lg` up,
 * where those controls are on screen anyway.
 *
 * Pages that render it must also leave room for it (`pb-24 lg:pb-8` or so),
 * otherwise it sits on top of the last row of content.
 */
const MobileActionBar: React.FC<MobileActionBarProps> = ({ actions }) => {
    if (actions.length === 0) return null;

    return (
        <nav className="mobile-action-bar" aria-label="Quick actions">
            <div className="flex items-stretch gap-2 max-w-lg mx-auto">
                {actions.map((action) => {
                    const className = `mobile-action-btn ${
                        action.primary
                            ? 'mobile-action-btn-primary'
                            : action.active
                                ? 'mobile-action-btn-active'
                                : ''
                    }`;

                    const content = (
                        <>
                            <FontAwesomeIcon icon={action.icon} className="text-base" aria-hidden="true" />
                            <span className="truncate max-w-full">{action.label}</span>
                        </>
                    );

                    if (action.to && !action.onClick) {
                        return (
                            <Link key={action.key} to={action.to} className={className}>
                                {content}
                            </Link>
                        );
                    }

                    return (
                        <button
                            key={action.key}
                            type="button"
                            onClick={action.onClick}
                            disabled={action.disabled}
                            aria-current={action.active ? 'page' : undefined}
                            className={className}
                        >
                            {content}
                        </button>
                    );
                })}
            </div>
        </nav>
    );
};

export default MobileActionBar;
